import { getStrapiURL } from '@/api/api';
import { showDailyValueResume } from '@/helpers/estimate';
import { usaFormatToBrFormatExtended } from '@/helpers/fdate';
import { OptionalType } from '@/types';
import Alert from 'components/Alert';
import { useReservation } from 'contexts/reservation';
import money from 'helpers/money';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import styles from 'styles/ReservationResume.module.css';
import Optional from '../ReservationResume/Optional';
import PercentOff from '../PercentOff';

import XIcon from 'public/icons/x.svg';
import { useSettings } from '@/contexts/settings';
import { Estimate } from '@/types/coral-types';
import { useLayout } from '@/contexts/layout';
import { useSnapshot } from 'valtio';
import reservation_state from '@/contexts/reservation_state';

import Arrow from "public/icons/arrowUp.svg"
import { useCashback } from '@/contexts/cashback';
import useDisplayValue from '@/hooks/useDisplayValue';
import CashbackAlert from '@/components/CashbackAlert';

type ReservationResumeProps = {
  onLoadingStart?: () => void;
  onLoadingEnd?: () => void;
  allOptionals: OptionalType[],
  hideTotal?: boolean;
  variant?: string
  className?: string;
  hideVehicleInfo?: boolean;
  hideRentalValues?: boolean;
  isMobileOpen?: boolean;
  onClose?: () => void;
  estimate?: Estimate;
  overrideTotal?: number;
  showPreAuthorization?: boolean;
  isSquareBorder?: boolean;
  installmentText?: string;
  interestAmount?: number;
  showCashbackAlert?: boolean;
};

export default function PaymentResume({
  onLoadingStart,
  onLoadingEnd,
  allOptionals,
  hideTotal = false,
  variant,
  className,
  hideVehicleInfo = false,
  hideRentalValues = false,
  isMobileOpen,
  onClose,
  estimate: estimateReservation,
  overrideTotal,
  showPreAuthorization = false,
  isSquareBorder,
  installmentText,
  interestAmount,
  showCashbackAlert,
}: ReservationResumeProps) {
  const router = useRouter();
  const {
    estimate,
    itinerary,
    optionals,
    depot,
    vehicle,
    returnDepot,
    paymentMethod,
    isEstimateLoading
  } = useReservation();
  const { setIsPaymentResumeOpen } = useLayout();
  const snap = useSnapshot(reservation_state);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const mobileFooterRef = useRef<HTMLDivElement>(null);
  const [mobileFooterHeight, setMobileFooterHeight] = useState(0);
  const [isPreAuthVisible, setIsPreAuthVisible] = useState(false);
  const userClosedPreAuthRef = useRef(false);
  const [isScrolledToEnd, setIsScrolledToEnd] = useState(false);

  const display = useDisplayValue({
    width: 150,
  });

  useEffect(() => {
    if (isMobileOpen && mobileFooterRef.current) {
      const observer = new ResizeObserver(entries => {
        for (let entry of entries) {
          setMobileFooterHeight(entry.target.clientHeight);
        }
      });
      observer.observe(mobileFooterRef.current);
      return () => observer.disconnect();
    }
  }, [isMobileOpen]);


  useEffect(() => {
    if (isMobileOpen) {
      setIsPreAuthVisible(false);
      userClosedPreAuthRef.current = false;
    }
  }, [isMobileOpen]);

  useEffect(() => {
    const scrollableElement = scrollContainerRef.current;
    if (!isMobileOpen || !scrollableElement) {
      return;
    }

    const handleScroll = () => {
      if (scrollContainerRef.current) {
        const el = scrollContainerRef.current;
        const isAtBottomCheck = el.scrollHeight - el.scrollTop <= el.clientHeight + 5;
        setIsScrolledToEnd(isAtBottomCheck);

        if (isAtBottomCheck) {
          if (!userClosedPreAuthRef.current) {
            setIsPreAuthVisible(true);
          }
        } else {
          userClosedPreAuthRef.current = false;
        }
      }
    };

    const timer = setTimeout(() => {
      if (scrollContainerRef.current) {
        const element = scrollContainerRef.current;
        const isScrollable = element.scrollHeight > element.clientHeight;
        setIsScrolledToEnd(!isScrollable);
        if (!isScrollable) {
          if (!userClosedPreAuthRef.current) {
            setIsPreAuthVisible(true);
          }
        } else {
          element.addEventListener('scroll', handleScroll);
          handleScroll();
        }
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      if (scrollableElement) {
        scrollableElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, [isMobileOpen]);

  const togglePreAuthVisibility = (e: React.MouseEvent) => {
    e.stopPropagation();
    const newVisibility = !isPreAuthVisible;
    setIsPreAuthVisible(newVisibility);
    userClosedPreAuthRef.current = !newVisibility;
  };

  const handleContainerClick = () => {
    if (isPreAuthVisible) {
      setIsPreAuthVisible(false);
      userClosedPreAuthRef.current = true;
    }
  };

  const estimateCar = estimateReservation ? estimateReservation : estimate;
  const optionalsCar = optionals ? optionals : snap.optionals

  const [isLoading] = useState(false);
  const [hasUpdateError] = useState(false);

  const { creditCardDiscount, pixDiscount, vantagens, specialProtection, superiorProtection, basicProtection } = useSettings();

  const isSpecialProtectionSelected = useMemo(() => {
    if (!optionalsCar) {
      return false;
    }
    return optionalsCar.some(
      opt =>
        (specialProtection?.coral_code && opt.code === specialProtection.coral_code) ||
        (superiorProtection?.coral_code && opt.code === superiorProtection.coral_code)
    );
  }, [optionalsCar, specialProtection, superiorProtection]);

  const preAuthorizationValue = useMemo(() => {
    const baseValue = estimateCar?.['ATH PAD']?.total ?? 0;
    if (isSpecialProtectionSelected) {
      return baseValue / 2;
    }
    return baseValue;
  }, [estimateCar, isSpecialProtectionSelected]);

  const isReservationComplete = router.pathname.startsWith('/pagamento') || router.pathname.startsWith('/reserva');

  const discountValue = useMemo(() => {
    if (paymentMethod === 'creditCard') {
      if (estimateCar && estimateCar.DAYS) {
        return Math.ceil((estimateCar.DAYS.total * creditCardDiscount) / 100 * 100) / 100;
      }
    } else if (paymentMethod === 'pix') {
      if (estimateCar && estimateCar.DAYS) {
        return Math.ceil((estimateCar.DAYS.total * pixDiscount) / 100 * 100) / 100;
      }
    }

    return 0;
  }, [paymentMethod, estimateCar, creditCardDiscount, pixDiscount]);

  const unroundedDiscountedTotalForDailies = useMemo(() => {
    if (estimateCar && estimateCar.DAYS) {
      const percentageOff = paymentMethod === 'pix' ? pixDiscount : paymentMethod === 'creditCard' ? creditCardDiscount : 0;
      return estimateCar.DAYS.total * (1 - (percentageOff / 100));
    }
    return estimateCar?.DAYS?.total || 0;
  }, [estimateCar, paymentMethod, pixDiscount, creditCardDiscount]);

  const couponPercentage = useMemo(() => {
    return estimateCar && estimateCar.DISCNT && estimateCar.DAYS?.total
      ? Math.floor(Math.abs(Math.floor(estimateCar.DISCNT.total + discountValue)) / estimateCar.DAYS.total * 100)
      : 0;
  }, [estimateCar, discountValue])

  useEffect(() => {
    if (isLoading) {
      if (typeof onLoadingStart !== 'undefined') {
        onLoadingStart();
      }
    } else {
      if (typeof onLoadingEnd !== 'undefined') {
        onLoadingEnd();
      }
    }
  }, [isLoading, onLoadingEnd, onLoadingStart]);

  useEffect(() => {
    setIsPaymentResumeOpen(!!isMobileOpen);

    if (isMobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [isMobileOpen, setIsPaymentResumeOpen])

  const objectToQuery = (object: any): string => {
    return Object.keys(object)
      .map(key => {
        if (key === 'extras') {
          if (Array.isArray(object.extras)) {
            return object.extras
              .map((extra: any) => `extras=${encodeURIComponent(extra)}`)
              .join('&');
          }
        }

        return `${key}=${encodeURIComponent(object[key])}`;
      })
      .join('&');
  };

  const [protections, regular] = useMemo(() => {
    let currentOpts = optionalsCar ? [...optionalsCar] : [];

    const hasRealProtection = currentOpts.some(opt => {
      const found = allOptionals.find(o => o.coral_code === opt.code);
      return found && found.category === 'Proteção';
    });

    if (!hasRealProtection && basicProtection) {
      if (!currentOpts.some(o => o.code === '__basic__')) {
        currentOpts.push({ code: '__basic__', qtty: 1 } as any);
      }
    }

    const optionalSelected = currentOpts.map(optional => {
      let findOptional = allOptionals.find(o => o.coral_code === optional.code);

      if (optional.code === '__basic__' && basicProtection) {
        findOptional = {
          ...basicProtection,
          coral_code: '__basic__',
          category: 'Proteção',
          rate_type: '',
        } as any;
      } else if (findOptional) {
        findOptional = {
          ...findOptional,
          rate_type: '',
        };
      }
      return {
        ...optional,
        optional: findOptional,
      };
    }).filter(o => o.optional);

    return [
      optionalSelected.filter(o => o.optional?.category === 'Proteção'),
      optionalSelected.filter(o => o.optional?.category !== 'Proteção'),
    ];
  }, [optionalsCar, allOptionals, basicProtection]);

  let totalToShow = overrideTotal !== undefined ? overrideTotal : estimateCar?.total;

  return (
    <div className={`${styles.payment_resume} ${isMobileOpen ? styles.mobile_open : ''}`}>
      <div className={`${styles.main__container__resume} ${className}`} onClick={handleContainerClick}>
        <div className={styles.item__title__container}>
          <h3 className={styles.item__title__resume}>Resumo</h3>
          {isMobileOpen && <XIcon className={styles.x_icon} onClick={onClose} />}
        </div>
        <div className={styles.resume__container} ref={scrollContainerRef} style={{ paddingBottom: isMobileOpen ? `${mobileFooterHeight}px` : '0px' }}>
          {!hideVehicleInfo && (
            vehicle ? (
              <>
                <div className={styles.payment__resume__title}>
                  <div className={styles.payment__resume__wrapp}>
                    <p className={styles.item__title__top}>Categoria</p>
                    <p className={styles.bold__text__subtitle__resume}>
                      {vehicle.main_car}
                    </p>
                    <p className={styles.payment__resume__inner__subtitle}>
                      {vehicle!.similar_car}
                    </p>
                  </div>
                  {!isReservationComplete && (
                    <div>
                      <Link href={`/veiculos?${objectToQuery(router.query)}`} legacyBehavior>
                        <a className={styles.reservationBar__step__edit}>Editar</a>
                      </Link>
                    </div>
                  )}
                </div>
                <div className={styles.car__image__container}>
                  <Image
                    src={
                      vehicle?.image?.url
                        ? getStrapiURL(vehicle.image.url)
                        : 'https://cms.aluguefoco.com.br/uploads/deufalt_cd5586fcac.png'
                    }
                    alt={vehicle?.image?.alternativeText ?? ""}
                    width={210}
                    height={128}
                    className={styles.car__image}
                  />
                </div>
                <hr className={styles.content__divider} />
              </>
            ) : (
              <>
                <Skeleton height={40} width={100} />
                <Skeleton height={20} width={200} />
                <Skeleton height={20} width={150} />
                <hr className={styles.content__divider} />
              </>
            )
          )}

          {hasUpdateError && (
            <div className="mt-12">
              <Alert
                variant="warning"
                message="Não foi possível atualizar o preço da reserva, este valor pode não ser o valor final"
              />
            </div>
          )}

          {itinerary && depot ? (
            <div className={styles.content__item}>

              <div className={styles.item__inner}>
                <p className={styles.inner__title}>Retirada</p>
                <p className={styles.bold__text__subtitle__resume}> {usaFormatToBrFormatExtended(itinerary.pickup_date)} -{' '}
                  {itinerary.pickup_time}</p>
                <div className={styles.item__data__wrap}>
                  <p className={styles.item__data__title__resume}>{depot.title}</p>
                  {!isReservationComplete && (
                    <div className={styles.item__data__value}>
                      <Link href={`/?${objectToQuery(router.query)}`} legacyBehavior>
                        <a className={styles.reservationBar__step__edit}>Editar</a>
                      </Link>
                    </div>
                  )}
                </div>
              </div>
              <div className={styles.item__inner}>
                <p className={styles.inner__title}>Devolução</p>
                <p className={styles.bold__text__subtitle__resume}>{usaFormatToBrFormatExtended(itinerary.return_date)} -{' '}
                  {itinerary.return_time}</p>
                <div className={styles.item__data__wrap}>
                  <p className={styles.item__data__title__resume}>{returnDepot?.title}</p>
                  {!isReservationComplete && (
                    <div className={styles.item__data__value}>
                      <Link href={`/?${objectToQuery(router.query)}`} legacyBehavior>
                        <a className={styles.reservationBar__step__edit}>Editar</a>
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.content__item}>
              <Skeleton height={20} width={200} />
              <Skeleton height={20} width={150} />
              <Skeleton height={20} width={200} />
              <Skeleton height={20} width={150} />
            </div>
          )}

          {!hideRentalValues && (
            estimateCar ? (
              <>
                <hr className={styles.content__divider} />
                <div className={styles.content__item}>
                  <h3 className={styles.item__title__middle}>Valores da locação</h3>
                  <div className={styles.item__inner}>
                    <div className={styles.item__data__wrap}>
                      {paymentMethod !== 'pospaid' ? (
                        <div className={styles.daily__value__container}>
                          <div className={styles.daily__value}>
                            <div className={styles.wrap__discount__value}>
                              <p className={styles.daily__text}>Diárias</p>
                              <p className={styles.pre__payment__text}>
                                Desconto Pré-pagamento
                              </p>
                              <span className={styles.item__data__title__resume__line_through}>
                                {isEstimateLoading ? <Skeleton width={100} /> : (
                                  estimateCar.DAYS && estimateCar.DAYS.total !== undefined && estimateCar.DAYS.quantity !== undefined ? (
                                    showDailyValueResume(
                                      estimateCar.DAYS.total,
                                      Number(estimateCar.DAYS.quantity),
                                    )
                                  ) : (
                                    <Skeleton width={100} />
                                  )
                                )}
                              </span>
                            </div>

                            <span className={styles.item__data__title__resume__discount__value}>
                              {isEstimateLoading ? <Skeleton width={100} /> : (
                                estimateCar.DAYS && estimateCar.DAYS.total !== undefined && estimateCar.DAYS.quantity !== undefined ? (
                                  showDailyValueResume(
                                    unroundedDiscountedTotalForDailies,
                                    Number(estimateCar.DAYS.quantity),
                                  )
                                ) : (
                                  <Skeleton width={100} />
                                )
                              )}
                            </span>
                          </div>

                          <div className={styles.item__data__value}>
                            <p className={styles.total__text}>Total</p>
                            <PercentOff />
                            {isEstimateLoading ? <Skeleton width={50} /> : (
                              estimateCar.DAYS && estimateCar.DAYS.total !== undefined ? (
                                <p className={styles.item__data__title__resume__line_through}>{money(estimateCar.DAYS.total)}</p>
                              ) : (
                                <Skeleton width={50} />
                              )
                            )}
                            {isEstimateLoading ? <Skeleton width={50} /> : (
                              estimateCar.DAYS && estimateCar.DAYS.total !== undefined ? (
                                <p className={styles.item__data__title__resume__discount}>{money(estimateCar.DAYS.total - discountValue)}</p>
                              ) : (
                                <Skeleton width={50} />
                              )
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className={styles.daily__value__container}>
                          <div className={styles.daily__value}>
                            <p className={styles.daily__text}>Diárias</p>
                            <span className={styles.item__data__title__resume}>
                              {isEstimateLoading ? <Skeleton width={100} /> : (
                                estimateCar.DAYS && estimateCar.DAYS.total !== undefined && estimateCar.DAYS.quantity !== undefined ? (
                                  showDailyValueResume(
                                    estimateCar.DAYS.total,
                                    Number(estimateCar.DAYS.quantity)
                                  )
                                ) : (
                                  <Skeleton width={100} />
                                )
                              )}
                            </span>
                          </div>
                          <div className={styles.item__data__value}>
                            <p className={styles.total__text}>Total</p>
                            {isEstimateLoading ? <Skeleton width={50} /> : (
                              estimateCar.DAYS && estimateCar.DAYS.total !== undefined ? (
                                <p className={styles.item__data__title__resume}>{money(estimateCar.DAYS.total)}</p>
                              ) : (
                                <Skeleton width={50} />
                              )
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {estimateCar?.LDW?.total > 0 && (
                    <div className={`${styles.item__inner} ${styles.section__spacing}`}>
                      <div className={styles.item__data__wrap}>
                        <div className={styles.protection__value}>
                          <p className={styles.item__data__title__resume}>Proteção LDW/CDW</p>
                        </div>
                        <div className={styles.item__data__value}>
                          {isEstimateLoading ? <Skeleton width={50} /> : money(estimateCar.LDW.total)}
                        </div>
                      </div>
                    </div>
                  )}

                  {protections && protections.length > 0 && (
                    <div className={styles.section__spacing}>
                      <p className={styles.protection__text}>Proteções</p>
                      {protections.map((optional) => (
                        <Optional
                          key={optional.code}
                          usePipe={false}
                          optional={optional}
                          estimate={estimateCar}
                        />
                      ))}
                    </div>
                  )}

                  {regular && regular.length > 0 && (
                    <div className={styles.section__spacing}>
                      <p className={styles.optional__text}>Opcionais</p>
                      {regular.map((optional) => (
                        <Optional
                          key={optional.code}
                          usePipe={false}
                          optional={optional}
                          estimate={estimateCar}
                        />
                      ))}
                    </div>
                  )}

                  {estimateCar.CASHBACK_DISCNT && Math.abs(estimateCar.CASHBACK_DISCNT.total) > 0 && (
                    <div className={styles.section__spacing}>
                      <p className={styles.optional__text}>Descontos Adicionais</p>
                      <div className={styles.item__inner}>
                        <div className={styles.item__data__wrap}>
                          <p className={styles.item__data__title__resume}>Cashback Volta Certa</p>
                          <div className={styles.item__data__value}>
                            <p className={styles.discount__text__red}>
                              {display(money(estimateCar.CASHBACK_DISCNT.total), isEstimateLoading)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {estimateCar['Drop Charge']?.total > 0 && (
                    <div className={`${styles.item__inner} ${styles.section__spacing}`}>
                      <div className={styles.item__data__wrap}>
                        <p className={styles.tax__dropOff}>Taxa de Drop off</p>
                        <div className={styles.item__data__value}>
                          {isEstimateLoading ? <Skeleton width={50} /> : money(estimateCar['Drop Charge']?.total)}
                        </div>
                      </div>
                    </div>
                  )}

                  {(estimateCar.DISCNT && (couponPercentage > 0) && Math.abs(Math.floor(estimateCar.DISCNT.total + discountValue)) > 0) && (
                    <div className={`${styles.item__inner} ${styles.section__spacing}`}>
                      <div className={styles.item__data__wrap}>
                        <div className={styles.daily__value}>
                          <p className={styles.daily__text}>
                            Descontos
                          </p>
                        </div>
                      </div>

                      {(estimateCar.DISCNT && (couponPercentage > 0) && Math.abs(Math.floor(estimateCar.DISCNT.total + discountValue)) > 0) ? (
                        <div className={styles.item__inner}>
                          <div className={styles.item__data__wrap}>
                            <div className={styles.discount__value}>
                              <p className={styles.item__data__title__resume}>
                                Cupom ({couponPercentage}%)
                              </p>
                            </div>
                            <div className={styles.item__data__value}>
                              <p className={styles.discount__text__red}>
                                {isEstimateLoading ? <Skeleton width={50} /> : money((estimateCar.DISCNT.total + discountValue))}
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )}

                  {estimateCar.ADMIN && estimateCar.ADMIN.total > 0 ? (
                    <div className={`${styles.item__inner} ${styles.section__spacing}`}>
                      <div className={styles.item__data__wrap}>
                        <div className={styles.protection__value}>
                          <p className={styles.tax__text}>Taxa de serviço</p>
                        </div>

                        <div className={styles.item__data__value}>
                          {isEstimateLoading ? <Skeleton width={50} /> : money(estimateCar!.ADMIN.total)}
                        </div>
                      </div>
                    </div>
                  ) : (
                    estimateCar.ADMIN && estimateCar.ADMIN.total === 0 ? null : (
                      <Skeleton height={20} width={150} />
                    )
                  )}
                </div>
              </>
            ) : (
              <>
                <hr className={styles.content__divider} />
                <div className={styles.content__item}>
                  <h3 className={styles.item__title__middle}>Valores da locação</h3>
                  <Skeleton height={20} width={200} />
                  <Skeleton height={20} width={150} />
                  <Skeleton height={20} width={200} />
                  <Skeleton height={20} width={150} />
                </div>
              </>
            )
          )}
        </div>
        <div ref={mobileFooterRef} className={`${variant === 'footer__gray' ? styles.footer__gray : ''} ${styles.content__item__total} ${isSquareBorder ? styles.square_border : ''}`}>
          <div className={styles.total__container}>
            {isMobileOpen && (
              <Arrow
                className={`${styles.arrowUp} ${isScrolledToEnd || isPreAuthVisible ? styles.arrowRotated : ''}`}
                onClick={togglePreAuthVisibility}
              />
            )}
            <div className={styles.item__data__wrap__total}>
              {hideTotal ? (
                <div className={styles.payment__footer__container}>
                  <p className={styles.footer__text}>{vantagens.VantagensFoco_Text}</p>
                  {vantagens.VantagensFoco.map((vantagem) => (
                    <div key={vantagem.text} className={styles.footer__item}>
                      {vantagem.Icon &&
                        <Image
                          src={getStrapiURL(vantagem.Icon.url)}
                          alt='foto'
                          width={12}
                          height={12}
                          className={styles.home__image}
                        />
                      }
                      <p className={styles.item__text}>{vantagem.text}</p>
                    </div>
                  ))}
                </div>
              ) : interestAmount && interestAmount > 0 ? (
                <>
                  <div className={styles.item__data__wrap}>
                    <p className={styles.interest__details__label}>Valor Total Previsto</p>
                    <p className={styles.interest__details__value}>
                      {isEstimateLoading ? <Skeleton width={100} /> : money((totalToShow ?? 0) - interestAmount)}
                    </p>
                  </div>
                  <div className={styles.item__data__wrap}>
                    <p className={styles.interest__details__label}>Juros</p>
                    <p className={styles.interest__details__value}>
                      {isEstimateLoading ? <Skeleton width={100} /> : money(interestAmount)}
                    </p>
                  </div>
                  <hr className={styles.content__divider} />
                  <div className={styles.item__data__wrap}>
                    <p className={styles.total__label}>Valor Total:</p>
                    <div className={styles.item__data__value}>
                      <p className={styles.total__value}>{money(totalToShow ?? 0)}</p>
                      {paymentMethod === 'creditCard' && installmentText && totalToShow && totalToShow > 0 && (
                        <p className={styles.total__value__sub}>{installmentText}</p>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className={styles.item__data__wrap__total}>
                  <p className={styles.total__label}>Valor Total:</p>
                  <div className={styles.item__data__value}>
                    {isEstimateLoading ? <Skeleton width={100} height={30} /> : (
                      totalToShow !== undefined ? (
                        <>
                          <p className={styles.total__value}>{money(totalToShow ?? 0)}</p>
                          {paymentMethod === 'creditCard' && installmentText && totalToShow > 0 &&
                            <p className={styles.total__value__sub}>{installmentText}</p>
                          }
                        </>
                      ) : (
                        <Skeleton width={100} height={30} />
                      )
                    )}
                  </div>
                </div>
              )}
            </div>
            {isMobileOpen && showPreAuthorization && (
              <div className={`${styles.pre__authorization__container_mobile} ${isPreAuthVisible ? styles.pre__authorization__container_visible : styles.pre__authorization__container_hidden}`}>
                <hr className={styles.content__divider} />
                <div className={styles.pre__authorization__header}>
                  <p className={styles.pre__authorization__title}>Pré-autorização</p>
                  <p className={styles.pre__authorization__value}>{money(preAuthorizationValue)}</p>
                </div>
                <p className={styles.pre__authorization__text}>
                  Na retirada do veículo, é feita uma pré-autorização no cartão de crédito do titular da reserva, que é liberada na devolução do veículo.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showCashbackAlert && (
        <div className={styles.alertCashbackMobile}>
          <CashbackAlert fullWidth />
        </div>
      )}

      {!isMobileOpen && showPreAuthorization && (
        <div className={styles.pre__authorization__container}>
          <div className={styles.pre__authorization__header}>
            <p className={styles.pre__authorization__title}>Pré-autorização</p>
            <p className={styles.pre__authorization__value}>{money(preAuthorizationValue)}</p>
          </div>
          <p className={styles.pre__authorization__text}>
            Na retirada do veículo, é feita uma pré-autorização no cartão de crédito do titular da reserva, que é liberada na devolução do veículo.
          </p>
        </div>
      )}
    </div>
  );
}
